import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { useToasts } from 'react-toast-notifications';
import { useHistory } from '@treams/treams-routes';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import GoalCreateCompetency from 'packages/kakapo-ui-kit/components/goals/create/GoalCreateCompetency';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import { PageTitle, PageTitleSub, SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import { Paragraph } from '@treams/treams-ui';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import { ActionArchiveListItem } from 'packages/kakapo-ui-kit/components/actions/ActionListItem';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import { useEffect, useState } from 'react';

const CompetencyContainer = styled(Flex)`
	> div:not(:first-child) {
		flex: 1;
		max-width: 500px;
	}
`;

const JobTitlesContainer = styled(Flex)`
	> div:not(:first-child) {
		flex: 1;
	}
`;

function GoalTemplateEditor(props: {
	goalTemplate?: graphql.GoalTemplate;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();
	const { localize } = useLocalize();

	const jobTitles = graphql.useJobTitlesQuery({
		variables: {
			first: 1000,
			isArchived: false,
		},
	});

	const jobTitleOptions = jobTitles.data?.jobTitles.edges.map(({ node }) => ({
		value: node.id,
		label: localize(node.title).text,
	}));
	const [selectedJobTitles, setSelectedJobTitles] = useState<{ value: string }[]>([]);
	
	useEffect(() => {
		if (jobTitleOptions && props.goalTemplate) {
			setSelectedJobTitles(jobTitleOptions.filter((option) => 
				!!(props.goalTemplate?.jobTitles ?? []).find((jobTitle) => jobTitle.id === option.value)));
		}
	}, [jobTitles]);

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateGoalTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateGoalTemplateMutation();

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			title: props.goalTemplate?.title ?? [],
			description: props.goalTemplate?.description ?? [],
			competency: props.goalTemplate?.competency?.competency as any,
			competencyLevelInput: (props.goalTemplate?.competency
				? {
						competency: props.goalTemplate?.competency?.competencyLevelIndex,
				  }
				: undefined) as any,
		},
		onSubmit: () => onUpdate(),
	});

	const onUpdate = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			description: formik.values.description?.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			competency:
				formik.values.competency && formik.values.competencyLevelInput
					? {
							competencyID: formik.values.competency.id,
							competencyLevelIndex: formik.values.competencyLevelInput.competency,
						}
					: undefined,
			relativeDueDate: undefined,
			jobTitleIDs: selectedJobTitles.map((option) => option.value),
		};

		if (props.goalTemplate) {
			await update({
				variables: {
					input: {
						id: props.goalTemplate.id,
						...input
					},
				},
			});
		} else {
			const { data } = await create({
				variables: { input },
			});

			if (data) {
				history.push(`/settings/goals/${data.createGoalTemplate.goalTemplate.id}`);
			}
		}

		addToast(t('common.settings.goals.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const valid = formik.values.title.some((translation) => translation.text.length > 0);

	return (
		<Flex flexDirection="column" mt={3}>
			<Flex flexDirection="row" alignItems="center">
				<PageTitle>
					{t(`common.settings.goals.${props.goalTemplate ? 'edit' : 'create'}.title`)}
				</PageTitle>
			</Flex>
			<Box mt={4} mb={2}>
				<Box mb={3}>
					<SubSectionTitle>{t('common.settings.goals.edit.details')}</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				<Form>
					<DetailsTableForm
						formValues={[
							{
								label: t('common.settings.goals.edit.name'),
								name: 'title',
								type: DetailsTableFormValueTypes.Multilingual,
							},
							{
								label: t('common.settings.goals.edit.description'),
								name: 'description',
								type: DetailsTableFormValueTypes.MultilingualQuill,
							},
						]}
					/>
					<CompetencyContainer my={4} alignItems="center">
						<PageTitleSub style={{ width: '237px' }}>{t('common.settings.goals.edit.competency')}</PageTitleSub>
						<GoalCreateCompetency standalone  />
					</CompetencyContainer>
					<JobTitlesContainer my={4} alignItems="center">
						<PageTitleSub style={{ width: '237px' }}>{t('common.settings.goals.edit.jobTitles.label')}</PageTitleSub>
						<Box>
							<Paragraph mb={2}>{t('common.settings.goals.edit.jobTitles.hint')}</Paragraph>
							<Select
								options={jobTitleOptions ?? []}
								unspaced
								onChange={(selection) => setSelectedJobTitles([...selection])}
								value={selectedJobTitles}
								closeMenuOnSelect
								blurInputOnSelect
								isSearchable
								isMulti
								multi
							/>
						</Box>
					</JobTitlesContainer>
				</Form>
			</FormikProvider>

			<Flex mt={4}>
				<Button
					size={32}
					onClick={() => formik.submitForm()}
					isLoading={loading}
					enabled={!loading && valid}
				>
					{t('common.save')}
				</Button>
			</Flex>

			<Flex mt={4}>
				{props.goalTemplate && (
					<ActionArchiveListItem
						archivableItem={props.goalTemplate}
						archivableItemName={t('common.settings.goals.itemName')}
						archivableItemKey="goalTemplate"
						useArchive={graphql.useArchiveGoalTemplateMutation as any}
						useRestore={graphql.useRestoreGoalTemplateMutation as any}
					/>
				)}
			</Flex>
		</Flex>
	);
}

function GoalTemplateEditorID(props: { id: string }) {
	const { data, error, loading } = graphql.useGoalTemplateQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return <Spinner isLoading />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <GoalTemplateEditor goalTemplate={data!.goalTemplate as graphql.GoalTemplate} />;
	}
}

export { GoalTemplateEditor as GoalTemplateCreator };

export default GoalTemplateEditorID;
